import { Fragment, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { getClientDetectionEmails } from "../../Api_Qdrant/Fast_apis";
import { getFastApiErrorMessage } from "../../Api_Qdrant/fastApiError";
import Pagination from "../../app/components/Pagination/Pagination";
import "./ClientDetectionEmails.css";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  sent: "Sent",
  failed: "Failed",
  pending: "Pending",
};

function formatDate(iso) {
  if (!iso) return "—";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusTag({ status }) {
  const key = status || "pending";

  return (
    <span className={`ce-tag ce-tag-status ce-tag-status--${key}`}>
      <span className="ce-tag-dot" />
      {STATUS_LABELS[key] || key}
    </span>
  );
}

function ReviewTag({ isReview }) {
  return (
    <span
      className={`ce-tag ${
        isReview
          ? "ce-tag-review--done"
          : "ce-tag-review--pending"
      }`}
    >
      {isReview ? "Reviewed by Sales" : "Pending Review"}
    </span>
  );
}

export default function ClientDetectionEmails() {
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  const fetchEmails = useCallback(
    async (targetPage, targetPageSize) => {
      setLoading(true);

      try {
        const res = await getClientDetectionEmails(
          targetPage,
          targetPageSize
        );

        // Rate-limited: the interceptor already showed a friendly toast — just stop.
        if (res?.rateLimited) {
          setItems([]);
          return;
        }

        if (res?.success) {
          setItems(res.data?.items || []);
          setTotal(res.data?.total || 0);
          setTotalPages(res.data?.total_pages || 1);
          setCurrentPage(res.data?.page || targetPage);

          // Close expanded row after new data is loaded
          setExpandedId(null);
        } else {
          toast.error(
            getFastApiErrorMessage(res, "Couldn't load emails. Try again.")
          );
          setItems([]);
        }
      } catch (error) {
        console.error("Failed to fetch client detection emails:", error);

        toast.error(
          getFastApiErrorMessage(error, "Couldn't load emails. Try again.")
        );
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchEmails(currentPage, pageSize);
  }, [currentPage, pageSize, fetchEmails]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="ce-page">
      {/* Header */}
      <header className="ce-header">
        <div className="ce-header-content">
          <h1>Client Detection Emails</h1>

          <p className="ce-subtitle">
            Leads and messages detected from conversations
            {total > 0 && !loading ? ` · ${total} total` : ""}
          </p>
        </div>

        <button
          type="button"
          className="ce-refresh-btn"
          onClick={() => fetchEmails(currentPage, pageSize)}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {/* Table */}
      <div className="ce-table-wrap">
        {loading ? (
          <div className="ce-state">
            <div className="ce-loader" />
            <span>Loading emails…</span>
          </div>
        ) : items.length === 0 ? (
          <div className="ce-state">
            <p className="ce-state-title">No emails yet</p>

            <p className="ce-state-body">
              Captured leads and detection emails will appear here.
            </p>
          </div>
        ) : (
          <table className="ce-table">
            <colgroup>
              <col className="ce-col-width-sn" />
              <col className="ce-col-width-summary" />
              <col className="ce-col-width-address" />
              <col className="ce-col-width-status" />
              <col className="ce-col-width-review" />
              <col className="ce-col-width-date" />
            </colgroup>

            <thead>
              <tr>
                <th className="ce-col-sn">S.No</th>
                <th>Summary</th>
                <th>From → To</th>
                <th>Status</th>
                <th>Sales Review</th>
                <th>Created</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, idx) => {
                const isOpen = expandedId === item.id;

                const sn =
                  (currentPage - 1) * pageSize + idx + 1;

                return (
                  <Fragment key={item.id}>
                    <tr
                      className={`ce-row ${
                        isOpen ? "ce-row--open" : ""
                      }`}
                      onClick={() => toggleExpand(item.id)}
                    >
                      {/* Serial Number */}
                      <td className="ce-col-sn">
                        {sn}
                      </td>

                      {/* Email Summary */}
                      <td className="ce-summary-cell">
                        <div
                          className="ce-summary-text"
                          title={item.email_text || ""}
                        >
                          {item.email_text || "—"}
                        </div>
                      </td>

                      {/* From / To */}
                      <td className="ce-address-cell">
                        <div
                          className="ce-address-from"
                          title={item.email_from || ""}
                        >
                          {item.email_from || "—"}
                        </div>

                        <div className="ce-address-arrow">
                          →
                        </div>

                        <div
                          className="ce-address-to"
                          title={item.email_to || ""}
                        >
                          {item.email_to || "—"}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="ce-status-cell">
                        <StatusTag
                          status={item.email_status}
                        />
                      </td>

                      {/* Review */}
                      <td className="ce-review-cell">
                        <ReviewTag
                          isReview={item.is_review}
                        />
                      </td>

                      {/* Created */}
                      <td className="ce-date-cell">
                        {formatDate(item.created_at)}
                      </td>
                    </tr>

                    {/* Expanded Details */}
                  {isOpen && (
                  <tr className="ce-detail-row">
                    <td colSpan={6}>
                      <div className="ce-detail">
                        <div className="ce-detail-header">
                        
                          <span>
                            <strong>Created by:</strong>{" "}
                            {item.created_by || "—"}
                          </span>

                          <span>
                            <strong>Detection Date:</strong>{" "}
                            {formatDate(item.created_at)}
                          </span>
                        </div>

                        <div className="ce-detail-body">
                          <div className="ce-detail-label">
                            Email Content
                          </div>

                          <p className="ce-detail-text">
                            {item.email_text || "—"}
                          </p>

                          <div className="ce-detail-actions">
                            <button
                              type="button"
                              className="ce-client-details-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/showClientDetails", {
                                  state: {
                                    userId: item.user_id,
                                    detectionDate: item.created_at,
                                  },
                                });
                              }}
                            >
                              View Client Details
                              <span>→</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && items.length > 0 && (
        <div className="ce-pagination-wrap">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={(page) => {
              setExpandedId(null);
              setCurrentPage(page);
            }}
            onPageSizeChange={(size) => {
              setExpandedId(null);
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
}